// AI Models Hook - React hooks for AI model management
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { AIModel, SmartReply } from '../types/ai-types';
import { llmProviderService, LLMProviderService, isConversationalModel } from '../services/LLMProviderService';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/modules/auth/hooks/useAuth';


/**
 * @param onlyConversational Restricts the list to models fit to serve as a
 * conversational assistant's brain (`isConversationalModel` in `LLMProviderService`).
 * Screens that administer every model on record — not just the ones offered as a
 * brain — must leave this `false` (the default) so nothing gets hidden from them.
 */
export function useModels(tenantId?: string, includeInactive: boolean = false, onlyConversational: boolean = false) {
  const { t } = useTranslation(['workflows', 'common']);
  const [models, setModels] = useState<AIModel[]>([]);
  const [defaultModel, setDefaultModel] = useState<AIModel | null>(null);
  const [loading, setLoading] = useState(true);
  // Additive, optional fields -- existing callers that destructure only
  // {models, loading, refetch} are unaffected. T-16: PlatformAdminAIManagement.tsx needs a
  // way to show a persistent panel instead of relying on the toast below alone; the raw
  // error travels out so the caller can map it with its own `t` (mapPlatformAdminError
  // expects the caller's namespace, not this hook's).
  const [error, setError] = useState<unknown>(null);
  const [errorAt, setErrorAt] = useState<Date | null>(null);
  const { toast } = useToast();

  const auth = useAuth();

  // Use provided tenantId, or fall back to auth context tenantId, or null for platform view
  const effectiveTenantId = tenantId || auth?.tenantId || null;

  useEffect(() => {
    loadModels();
  }, [effectiveTenantId, includeInactive, onlyConversational]);

  const loadModels = async () => {
    try {
      setLoading(true);
      console.log('Loading models for tenant:', effectiveTenantId, 'includeInactive:', includeInactive);

      const [modelsData, defaultModelData] = await Promise.all([
        includeInactive
          ? LLMProviderService.getAllModels()
          : LLMProviderService.getActiveModels(),
        llmProviderService.getDefaultModel(effectiveTenantId)
      ]);

      console.log('Loaded models:', modelsData);
      console.log('Default model:', defaultModelData);

      setModels(onlyConversational ? modelsData.filter(isConversationalModel) : modelsData);
      setDefaultModel(defaultModelData);
      setError(null);
      setErrorAt(null);
    } catch (err) {
      console.error('Error loading models:', err);
      setError(err);
      setErrorAt(new Date());
      toast({
        title: t('common:error'),
        description: t('useModels.loadFailed'),
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return {
    models,
    defaultModel,
    loading,
    error,
    errorAt,
    refetch: loadModels
  };
}

export function useSmartReplies(conversationId: string) {
  const [smartReplies, setSmartReplies] = useState<SmartReply[]>([]);
  const [loading, setLoading] = useState(false);

  const generateReplies = async (
    conversationHistory: unknown[],
    customerMessage: string,
    modelId?: string
  ) => {
    try {
      setLoading(true);

      // Dynamic import to avoid circular dependency
      const { supabase } = await import('@/integrations/supabase/client');

      // Get tenant schema
      const { data: schema } = await supabase.rpc('get_user_tenant_schema');
      if (!schema) throw new Error('No tenant schema found');

      // Query database for templates (all active templates) via typed RPC.
      const { data, error } = await supabase.rpc('list_smart_reply_templates', {
        p_tenant_schema: schema,
        p_limit: 5,
      });

      if (error) throw error;

      // Transform to SmartReply format expected by component
      const replies = (data || []).map((item: Record<string, unknown>) => ({
        content: item.content,
        confidence: 1.0,
        context: item.template_name
      }));

      setSmartReplies(replies);
      return replies;
    } catch (error) {
      console.error('Smart replies not available:', error);
      // Fail silently - smart replies are optional feature
      setSmartReplies([]);
      return [];
    } finally {
      setLoading(false);
    }
  };

  const clearReplies = () => {
    setSmartReplies([]);
  };

  return {
    smartReplies,
    loading,
    generateReplies,
    clearReplies
  };
}

export function useMessageAnalysis() {
  const [analysis, setAnalysis] = useState<{
    intent: string;
    sentiment: number;
    entities: unknown[];
    confidence: number;
  } | null>(null);
  const [loading, setLoading] = useState(false);

  const analyzeMessage = async (message: string, modelId?: string) => {
    try {
      setLoading(true);
      const result = await LLMProviderService.analyzeMessage(message);
      setAnalysis({
        intent: result.intent,
        sentiment: 0,
        entities: [],
        confidence: result.confidence
      });
      return result;
    } catch (error) {
      console.error('Error analyzing message:', error);
      return null;
    } finally {
      setLoading(false);
    }
  };

  return {
    analysis,
    loading,
    analyzeMessage
  };
}

export function useAIResponse() {
  const [response, setResponse] = useState<string>('');
  const [loading, setLoading] = useState(false);

  const generateResponse = async (
    conversationHistory: unknown[],
    customerMessage: string,
    modelId?: string,
    options?: {
      tone?: 'professional' | 'friendly' | 'casual';
      maxLength?: number;
      includeProducts?: boolean;
    }
  ) => {
    try {
      setLoading(true);
      const result = await LLMProviderService.generateResponse(
        conversationHistory + ' ' + customerMessage
      );
      setResponse(result.content || '');
      return result;
    } catch (error) {
      console.error('Error generating AI response:', error);
      return '';
    } finally {
      setLoading(false);
    }
  };

  return {
    response,
    loading,
    generateResponse,
    clearResponse: () => setResponse('')
  };
}
